"use client";

import { createTheme } from "@mui/material/styles";
import type {} from "@mui/x-date-pickers/themeAugmentation";

/**
 * Tema MUI de justHi.
 *
 * Paleta del moodboard (JustHi - pantallas.png):
 *  - background:    casi negro (#0a0a0a)
 *  - cards/paper:   gris muy oscuro (#171717)
 *  - primary:       amarillo neon / lima (#D4FF00)
 *  - texto:         blanco (#fff) / gris suave (#A0A0A0)
 *  - divider:       gris oscuro casi invisible (#262626)
 */

export const justHiColors = {
  bg: "#0a0a0a",
  paper: "#171717",
  paperHover: "#1f1f1f",
  border: "#262626",
  borderHover: "rgba(212, 255, 0, 0.5)",
  yellow: "#D4FF00",
  yellowDark: "#B8E000",
  yellowLight: "#E4FF4D",
  textPrimary: "#ffffff",
  textSecondary: "#A0A0A0",
  textMuted: "#6B6B6B",
} as const;

export const theme = createTheme({
  zIndex: {
    mobileStepper: 2050,
    fab: 2050,
    speedDial: 2050,
    appBar: 2100,
    drawer: 2200,
    modal: 2300,
    snackbar: 2400,
    tooltip: 2500,
  },
  palette: {
    mode: "dark",
    primary: {
      main: justHiColors.yellow,
      light: justHiColors.yellowLight,
      dark: justHiColors.yellowDark,
      contrastText: "#000",
    },
    secondary: {
      main: justHiColors.yellowLight,
      light: "#F2FF80",
      dark: justHiColors.yellow,
      contrastText: "#000",
    },
    background: {
      default: justHiColors.bg,
      paper: justHiColors.paper,
    },
    text: {
      primary: justHiColors.textPrimary,
      secondary: justHiColors.textSecondary,
      disabled: justHiColors.textMuted,
    },
    divider: justHiColors.border,
    error: { main: "#EF4444" },
    success: { main: "#10B981" },
    warning: { main: "#F59E0B" },
    info: { main: "#3B82F6" },
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
    },
  },
  typography: {
    fontFamily: "var(--font-geist-sans), system-ui, -apple-system, sans-serif",
    fontSize: 14,
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: justHiColors.bg,
          color: justHiColors.textPrimary,
        },
      },
    },
    MuiTextField: {
      defaultProps: { variant: "outlined", size: "small" },
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            backgroundColor: justHiColors.paper,
            color: justHiColors.textPrimary,
            fontSize: "0.875rem",
            "& fieldset": { borderColor: justHiColors.border, borderWidth: "1px" },
            "&:hover fieldset": { borderColor: justHiColors.borderHover },
            "&.Mui-focused fieldset": { borderColor: justHiColors.yellow, borderWidth: "2px" },
            "&.Mui-error fieldset": { borderColor: "#EF4444" },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: justHiColors.textSecondary,
            "&.Mui-focused": { color: justHiColors.yellow },
            "&.Mui-error": { color: "#EF4444" },
          },
          "& .MuiInputBase-input": {
            fontSize: "0.875rem",
            padding: "8.5px 14px",
            color: justHiColors.textPrimary,
            "&::placeholder": { color: justHiColors.textMuted, opacity: 1 },
          },
          "& .MuiInputBase-inputMultiline": { padding: "0px" },
        },
      },
    },
    MuiSelect: {
      defaultProps: { variant: "outlined", size: "medium" },
      styleOverrides: {
        root: {
          backgroundColor: justHiColors.paper,
          color: justHiColors.textPrimary,
          fontSize: "0.875rem",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.border, borderWidth: "1px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.borderHover },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.yellow, borderWidth: "2px" },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#EF4444" },
        },
        icon: { color: justHiColors.textSecondary },
      },
    },
    MuiFormControl: {
      defaultProps: { variant: "outlined", size: "small" },
      styleOverrides: {
        root: {
          "& .MuiInputLabel-root": {
            fontSize: "0.875rem",
            color: justHiColors.textSecondary,
            "&.Mui-focused": { color: justHiColors.yellow },
            "&.Mui-error": { color: "#EF4444" },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          color: justHiColors.textSecondary,
          "&.Mui-focused": { color: justHiColors.yellow },
          "&.Mui-error": { color: "#EF4444" },
        },
        asterisk: { color: "#EF4444" },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: justHiColors.paper,
          color: justHiColors.textPrimary,
          fontSize: "0.875rem",
          "& .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.border, borderWidth: "1px" },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.borderHover },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: justHiColors.yellow, borderWidth: "2px" },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#EF4444" },
        },
        input: { fontSize: "0.875rem", padding: "8.5px 14px", color: justHiColors.textPrimary },
        inputMultiline: { padding: "0px" },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: justHiColors.paper,
          color: justHiColors.textPrimary,
          fontSize: "0.875rem",
          "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: justHiColors.border, borderWidth: "1px" },
          "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: justHiColors.borderHover },
          "&.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": { borderColor: justHiColors.yellow, borderWidth: "2px" },
          "&.Mui-error .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#EF4444" },
        },
      },
    },
    MuiPickersTextField: {
      styleOverrides: {
        root: {
          "& .MuiPickersOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: justHiColors.yellow,
            borderWidth: "2px",
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: "0.875rem",
          color: justHiColors.textPrimary,
          "&:hover": { backgroundColor: "rgba(212, 255, 0, 0.08)" },
          "&.Mui-selected": {
            backgroundColor: "rgba(212, 255, 0, 0.16)",
            "&:hover": { backgroundColor: "rgba(212, 255, 0, 0.24)" },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none",
          backgroundColor: justHiColors.paper,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        root: { zIndex: 2300 },
        paper: { backgroundColor: justHiColors.paper, border: `1px solid ${justHiColors.border}` },
      },
    },
    MuiMenu: {
      styleOverrides: {
        root: { zIndex: 2300 },
        paper: { backgroundColor: justHiColors.paper, border: `1px solid ${justHiColors.border}` },
      },
    },
    MuiPopper: {
      styleOverrides: { root: { zIndex: 2300 } },
    },
    MuiModal: {
      styleOverrides: { root: { zIndex: 2300 } },
    },
    MuiDialog: {
      styleOverrides: {
        root: { zIndex: 2300 },
        paper: { backgroundColor: justHiColors.paper },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        popper: { zIndex: 2500 },
        tooltip: {
          backgroundColor: justHiColors.paper,
          color: justHiColors.textPrimary,
          border: `1px solid ${justHiColors.border}`,
          fontSize: "0.75rem",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: "0.75rem",
          marginLeft: 0,
          marginTop: "4px",
          color: justHiColors.textSecondary,
          "&.Mui-error": { color: "#EF4444" },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: "0.5rem",
          fontWeight: 600,
          fontSize: "0.875rem",
        },
        containedPrimary: {
          color: "#000",
          backgroundColor: justHiColors.yellow,
          "&:hover": { backgroundColor: justHiColors.yellowDark },
          "&.Mui-disabled": {
            backgroundColor: "rgba(212, 255, 0, 0.3)",
            color: "rgba(0, 0, 0, 0.6)",
          },
        },
        outlined: {
          borderColor: justHiColors.border,
          color: justHiColors.textPrimary,
          "&:hover": {
            borderColor: justHiColors.yellow,
            backgroundColor: "rgba(212, 255, 0, 0.08)",
          },
        },
        text: {
          color: justHiColors.textPrimary,
          "&:hover": { backgroundColor: "rgba(212, 255, 0, 0.08)" },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: justHiColors.textSecondary,
          "&.Mui-checked": { color: justHiColors.yellow },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: justHiColors.textSecondary,
          "&.Mui-checked": { color: justHiColors.yellow },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: justHiColors.yellow,
            "& + .MuiSwitch-track": { backgroundColor: justHiColors.yellow },
          },
        },
        track: { backgroundColor: justHiColors.border },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: justHiColors.border } },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: justHiColors.bg,
          borderBottom: `1px solid ${justHiColors.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: justHiColors.paper,
          borderRight: `1px solid ${justHiColors.border}`,
        },
      },
    },
  },
});
